import type { CmsAsset, RichTextContent } from '../../types/contentstack'
import type { ImageTextColumnsFields } from './types'

const PLACEHOLDER_CMS_IMAGE_URL =
  'https://eu-images.contentstack.com/v3/assets/blt249ad47e3410b9aa/bltf10b9205f4494730/69c539e5a3472f1f9f3236bb/george.jpeg'

const createAsset = (uid: string, filename: string, title: string): CmsAsset => ({
  uid,
  url: PLACEHOLDER_CMS_IMAGE_URL,
  title,
  filename,
  content_type: 'image/jpeg',
})

const text = (value: string, marks: { bold?: boolean; italic?: boolean } = {}) => ({
  text: value,
  ...marks,
})

const paragraph = (
  uid: string,
  children: Array<{ text: string; bold?: boolean; italic?: boolean }>,
) => ({
  type: 'p',
  uid,
  attrs: {},
  children,
})

const introBody: RichTextContent = {
  type: 'doc',
  uid: 'foodbox-intro-doc',
  attrs: {},
  children: [
    paragraph('foodbox-intro-p1', [
      text('Discover Gousto’s fresh take on mealtimes.', { bold: true }),
      text(
        ' Think of it as dinner made way easier. A Gousto food box is a meal kit packed with fresh ingredients, delivered right to your door—no more last-minute dashes to the shop or guessing how much coriander to use.',
      ),
    ]),
    paragraph('foodbox-intro-p2', [
      text(
        'Just pick the recipes you fancy, and we’ll send you exactly what you need (down to the last clove of garlic) along with easy-to-follow cooking steps. No waste, no stress, just tasty meals made simple.',
      ),
    ]),
    paragraph('foodbox-intro-p3', [
      text(
        'That’s why subscription food boxes are a super convenient and budget-friendly alternative to the weekly supermarket scramble.',
      ),
    ]),
  ],
} as unknown as RichTextContent

const makeDinnerABreezeBody: RichTextContent = {
  type: 'doc',
  uid: 'mdab-doc',
  attrs: {},
  children: [
    paragraph('mdab-p1', [
      text(
        'Tired of the nightly “what’s for dinner?” panic? Let Gousto swoop in like a dinnertime superhero. We deliver tasty, no-fuss food boxes straight to your door—so you can skip the stress and serve up something delicious without breaking a sweat.',
      ),
    ]),
  ],
} as unknown as RichTextContent

const readyToGoBody: RichTextContent = {
  type: 'doc',
  uid: 'doc_ready_to_go',
  attrs: {},
  children: [
    paragraph('p_ready_to_go_1', [
      text(
        'If you’re after the best value and the biggest choice, you’re in the right place. With over 175 mouthwatering recipes every single week, Gousto’s got something for every household, whether you’re cooking for one or feeding a family of five.',
      ),
    ]),
    paragraph('p_ready_to_go_2', [
      text(
        'Think global flavours, fancy gourmet nights, speedy 10-minute wonders and feel-good healthy picks - all delivered to your door. It’s like falling in love with dinner all over again.',
      ),
    ]),
  ],
} as unknown as RichTextContent

const richTextOfferBody = (uid: string, discount: string, suffix: string): RichTextContent =>
  ({
    type: 'doc',
    uid: `${uid}-doc`,
    attrs: {},
    children: [paragraph(`${uid}-p1`, [text(discount, { bold: true }), text(suffix)])],
  } as unknown as RichTextContent)

const richTextSubSectionBody = (uid: string, heading: string, bodyText: string): RichTextContent =>
  ({
    type: 'doc',
    uid: `${uid}-doc`,
    attrs: {},
    children: [paragraph(`${uid}-p1`, [text(heading, { bold: true }), text(` ${bodyText}`)])],
  } as unknown as RichTextContent)

export const imageTextColumnsIntroductionMockData: ImageTextColumnsFields = {
  heading: 'So, what’s a food box anyway?',
  heading_level: 'h2',
  image_position: 'right',
  image: createAsset('foodbox-desktop', 'foodbox-desktop.jpg', 'Gousto food box hero image'),
  image_mobile: createAsset(
    'foodbox-mobile',
    'foodbox-mobile.jpg',
    'Gousto food box hero image (mobile)',
  ),
  image_alt:
    'Gousto recipe box containing vegetables, meat, spices, and recipe cards, next to a creamy tomato sauce pasta cooked recipe.',
  body: introBody,
}

export const imageTextColumnsMakeDinnerABreezeMockData: ImageTextColumnsFields = {
  heading: 'Make Dinner a Breeze (Not a Battle)',
  heading_level: 'h3',
  image_position: 'left',
  image: createAsset('goustobox-closed', 'goustobox-closed.jpg', 'Gousto box at the door'),
  image_mobile: createAsset(
    'goustobox-closed-mobile',
    'goustobox-closed-mobile.jpg',
    'Gousto box at the door (mobile)',
  ),
  image_alt: 'Gousto box delivered at a customer’s door.',
  body: makeDinnerABreezeBody,
  cta: {
    text: 'Try Gousto with 50% off',
    url: '/signup',
    trigger_promocode: true,
    variant: 'primary',
  },
}

export const imageTextColumnsReadyToGiveGoustoAGoMockData: ImageTextColumnsFields = {
  heading: 'Ready to give Gousto a Go?',
  heading_level: 'h3',
  image_position: 'right',
  image: createAsset('ready-to-go-desktop', 'ready-to-go-desktop.jpg', 'Family sharing dinner'),
  image_mobile: createAsset(
    'ready-to-go-mobile',
    'ready-to-go-mobile.jpg',
    'Family sharing dinner (mobile)',
  ),
  image_alt: 'Family around a kitchen table sharing baked salmon, Greek salad and minted couscous',
  body: readyToGoBody,
  sub_sections: [
    {
      sub_heading: '50% OFF',
      sub_body: 'your first box',
      body: richTextOfferBody('first-box', '50% OFF', ' your first box'),
      show_tick_icon: true,
    },
    {
      sub_heading: '40% OFF',
      sub_body: 'your second',
      body: richTextOfferBody('second-box', '40% OFF', ' your second'),
      show_tick_icon: true,
    },
    {
      sub_heading: '20% OFF',
      sub_body: 'all other boxes for two whole months',
      body: richTextOfferBody(
        'remaining-boxes',
        '20% OFF',
        ' all other boxes for two whole months',
      ),
      show_tick_icon: true,
    },
  ],
  closing_body: "Now's definitely the time to give it a whirl.",
  cta: {
    text: 'Get your first box with 50% off now',
    url: '/signup',
    trigger_promocode: true,
    variant: 'primary',
  },
}

const richSubBody: RichTextContent = {
  type: 'doc',
  uid: 'rich-subbody-doc',
  attrs: {},
  children: [
    {
      type: 'p',
      uid: 'rich-subbody-p1',
      attrs: {},
      children: [
        { text: 'Big ', bold: true },
        { text: 'flavour', italic: true },
        { text: ', ' },
        { text: 'zero', underline: true },
        { text: ' stress — ' },
        {
          type: 'a',
          uid: 'rich-subbody-a1',
          attrs: { url: '/box-prices', target: '_self' },
          children: [{ text: 'see plans' }],
        },
      ],
    },
  ],
} as unknown as RichTextContent

// Exercises both sub_body shapes during the text→rich-text migration: a
// rich-text body (bold/italic/underline/link) under a plain sub heading, and a
// legacy plain-string sub_body. Neither sub-section sets the separate `body`
// field, so the sub_heading + sub_body render path is used.
export const imageTextColumnsRichSubBodyMockData: ImageTextColumnsFields = {
  heading: 'Sub bodies, now with rich text',
  heading_level: 'h3',
  image_position: 'left',
  image: createAsset(
    'rich-subbody-desktop',
    'rich-subbody-desktop.jpg',
    'Fresh Gousto ingredients arranged on a kitchen counter',
  ),
  image_mobile: createAsset(
    'rich-subbody-mobile',
    'rich-subbody-mobile.jpg',
    'Fresh Gousto ingredients arranged on a kitchen counter (mobile)',
  ),
  image_alt: 'Fresh Gousto ingredients arranged on a kitchen counter',
  sub_sections: [
    {
      sub_heading: 'Veggie or vegan?',
      sub_body: richSubBody,
      show_tick_icon: true,
    },
    {
      sub_heading: 'Feeding picky kids or hungry teens?',
      sub_body: 'Existing plain-text sub body keeps rendering unchanged.',
      show_tick_icon: true,
    },
  ],
}

export const imageTextColumnsWhyGoustoMockData: ImageTextColumnsFields = {
  heading: 'Why Gousto? Glad you asked',
  heading_level: 'h3',
  image_position: 'right',
  image: createAsset(
    'whygousto-desktop',
    'whygousto-desktop.jpg',
    'Customer cooking a Gousto recipe',
  ),
  image_mobile: createAsset(
    'whygousto-mobile',
    'whygousto-mobile.jpg',
    'Customer cooking a Gousto recipe (mobile)',
  ),
  image_alt: 'Customer serving a delicious Sweet Potato And Black Bean tomato Stew',
  sub_sections: [
    {
      sub_heading: 'No More Supermarket Stress',
      sub_body:
        'Forget wandering aimlessly through the aisles or caving into another sad takeaway. We’ve got you covered with recipes you’ll actually look forward to.',
      body: richTextSubSectionBody(
        'why-gousto-stress',
        'No More Supermarket Stress',
        'Forget wandering aimlessly through the aisles or caving into another sad takeaway. We’ve got you covered with recipes you’ll actually look forward to.',
      ),
    },
    {
      sub_heading: 'Tasty and Good for You',
      sub_body:
        'Food box delivery means fresh, wholesome ingredients delivered to your door that’ll make both your tastebuds and your health goals happy.',
      body: richTextSubSectionBody(
        'why-gousto-tasty',
        'Tasty and Good for You',
        'Food box delivery means fresh, wholesome ingredients delivered to your door that’ll make both your tastebuds and your health goals happy.',
      ),
    },
    {
      sub_heading: 'Meal Planning = Done',
      sub_body:
        'You get easy-to-follow recipes, all pre-measured and ready to go. No guesswork, no food waste, just good eats.',
      body: richTextSubSectionBody(
        'why-gousto-planning',
        'Meal Planning = Done',
        'You get easy-to-follow recipes, all pre-measured and ready to go. No guesswork, no food waste, just good eats.',
      ),
    },
  ],
  closing_body:
    'Oh, and did we mention? Our menu is the biggest out there, with over 175 recipes to choose from every week.',
  cta: {
    text: 'Join millions cooking better',
    url: '/signup',
    trigger_promocode: true,
    variant: 'primary',
  },
}

export const imageTextColumnsYouAreInControlMockData: ImageTextColumnsFields = {
  heading: 'You’re in Control',
  heading_level: 'h3',
  image_position: 'left',
  image: createAsset(
    'youincontrol-desktop',
    'youincontrol-desktop.jpg',
    'Variety of Gousto recipes',
  ),
  image_mobile: createAsset(
    'youincontrol-mobile',
    'youincontrol-mobile.jpg',
    'Variety of Gousto recipes (mobile)',
  ),
  image_alt:
    'Variety of Gousto recipes, ranging from Prepped in 5, to global cuisines, to healthy recipes and family friendly.',
  sub_sections: [
    {
      sub_heading: 'Veggie or vegan?',
      sub_body: 'We’ve got options galore.',
      body: richTextSubSectionBody(
        'you-control-veggie',
        'Veggie or vegan?',
        'We’ve got options galore.',
      ),
      show_tick_icon: true,
    },
    {
      sub_heading: 'Feeding picky kids or hungry teens?',
      sub_body: 'Tuck into family-friendly faves.',
      body: richTextSubSectionBody(
        'you-control-kids',
        'Feeding picky kids or hungry teens?',
        'Tuck into family-friendly faves.',
      ),
      show_tick_icon: true,
    },
    {
      sub_heading: 'Ingredient quality',
      sub_body:
        'We partner with trusted suppliers for 100% British meat and responsibly sourced ingredients.',
      body: richTextSubSectionBody(
        'you-control-quality',
        'Ingredient quality',
        'We partner with trusted suppliers for 100% British meat and responsibly sourced ingredients.',
      ),
      show_tick_icon: true,
    },
    {
      sub_heading: 'Cooking for one, two, or a whole hungry crew?',
      sub_body: 'Totally doable.',
      body: richTextSubSectionBody(
        'you-control-household',
        'Cooking for one, two, or a whole hungry crew?',
        'Totally doable.',
      ),
      show_tick_icon: true,
    },
    {
      sub_heading: 'Plans that flex with your schedule and taste buds?',
      sub_body: 'Absolutely',
      body: richTextSubSectionBody(
        'you-control-plans',
        'Plans that flex with your schedule and taste buds?',
        'Absolutely',
      ),
      show_tick_icon: true,
    },
  ],
  cta: {
    text: 'Get started with 50% off',
    url: '/signup',
    trigger_promocode: true,
    variant: 'primary',
  },
}
